import React, {
  useState, useEffect, useRef, useCallback, memo,
} from "react";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { IndexeddbPersistence } from "y-indexeddb";
import {
  FiSquare, FiCircle, FiMinus, FiDownload, FiRefreshCcw,
  FiTrash2, FiMove, FiPlus, FiMinusCircle, FiX, FiImage,
  FiMousePointer
} from "react-icons/fi";
import { FaEraser, FaPaintBrush } from "react-icons/fa";
import { toast } from "react-toastify";

// ─── constants ────────────────────────────────────────────────────────────────
const DEFAULT_MEDIA_W = 480;
const DEFAULT_MEDIA_H = 270;
const FLUSH_INTERVAL  = 50;   // ms — throttle Yjs stroke sync
const MIN_MOVE_DIST   = 0.6;  // px — ignore pointer jitter
const BASE_W = 1920;
const BASE_H = 1080;

// 🆕 NAYA LOGIC: Apna API Base URL yahan set karein (jaise http://192.168.1.9:8080)
const API_BASE_URL = import.meta.env.VITE_WAPI_URL || "http://localhost:8080";
// 🆕 FIX 1: URL Formatter
const getFullImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
  return url;
};

// ─── helpers ──────────────────────────────────────────────────────────────────
function uid() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// (Base64 helper ab sirf tab kaam aayega agar API fail ho jaye fallback ke liye, waise iski zaroorat nahi)
function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload  = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function pointToLineDist(p, v, w) {
  const l2 = (v.x - w.x)**2 + (v.y - w.y)**2;
  if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
  let t = ((p.x - v.x)*(w.x - v.x) + (p.y - v.y)*(w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (v.x + t*(w.x - v.x)), p.y - (v.y + t*(w.y - v.y)));
}

// ─── StreamerWhiteboard ───────────────────────────────────────────────────────
const StreamerWhiteboard = memo(
  ({
    sessionId,
    roomCode,
    wsToken,
    sessionInfo,
    isActive,
    onClose,
    allowViewersToDraw = true,
    mainScreenMode     = false,
    compact            = false,
  }) => {
    // ── canvas refs ──
    const canvasRef           = useRef(null);
    const backgroundCanvasRef = useRef(null);
    const ctxRef              = useRef(null);
    const bgCtxRef            = useRef(null);
    const containerRef        = useRef(null);

    // ── Yjs refs ──
    const yDocRef          = useRef(null);
    const yProviderRef     = useRef(null);
    const yWhiteboardRef   = useRef(null);
    const ySettingsRef     = useRef(null);
    const yUndoManagerRef  = useRef(null);
    const yObserverCleanup = useRef(null);

    // ── media caches ──
    const imageCacheRef = useRef(new Map());

    // ── raf / flush ──
    const rafIdRef        = useRef(null);
    const pendingObjsRef  = useRef(null);
    const flushTimerRef   = useRef(null);
    const pendingUpdateRef= useRef(null); // Used for dragging/resizing

    // ── stroke & shapes state ──
    const currentStrokeIdRef  = useRef(null);
    const strokeBufferRef     = useRef([]);
    const lastLocalPointRef   = useRef(null);
    const lastLocalMidRef     = useRef(null);
    const pointerIdRef        = useRef(null);
    const shapeStartRef       = useRef(null);

    // ── pan ──
    const lastPanPointRef  = useRef({ x: 0, y: 0 });
    const canvasOffsetRef  = useRef({ x: 0, y: 0 });
    const lastPointRef     = useRef({ x: 0, y: 0 });

    // ── selected object (for move/delete/resize) ──
    const selectedIdRef = useRef(null);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const isResizingRef = useRef(false);

    // ── state ──
    const [tool,            setTool]            = useState("pen");
    const [color,           setColor]           = useState("#000000");
    const [strokeWidth,     setStrokeWidth]     = useState(2);
    const [opacity,         setOpacity]         = useState(1);
    const [currentZoom,     setCurrentZoom]     = useState(1);
    const [isConnected,     setIsConnected]     = useState(false);
    const [participants,    setParticipants]    = useState([]);
    const [backgroundColor, setBackgroundColor] = useState("#ffffff");
    const [isGridVisible,   setIsGridVisible]   = useState(false);
    const [isPanning,       setIsPanning]       = useState(false);
    const [isDrawing,       setIsDrawing]       = useState(false);
    const [selectedId,      setSelectedId]      = useState(null);

    const fileInputRef       = useRef(null);

    // ═══════════════════════════════════════════════════════
    // Helpers
    // ═══════════════════════════════════════════════════════
    const getTransformedPoint = useCallback((e) => {
      if (!canvasRef.current) return { x: 0, y: 0 };
      const rect   = canvasRef.current.getBoundingClientRect();
      const scaleX = BASE_W / rect.width;
      const scaleY = BASE_H / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top)  * scaleY;
      return {
        x: (x - canvasOffsetRef.current.x) / currentZoom,
        y: (y - canvasOffsetRef.current.y) / currentZoom,
      };
    }, [currentZoom]);

    const getCurrentObjects = useCallback(() => {
      if (!yWhiteboardRef.current || yWhiteboardRef.current.length === 0) return [];
      return (yWhiteboardRef.current.toArray()[0]?.objects) || [];
    }, []);

    // ═══════════════════════════════════════════════════════
    // Yjs write helpers
    // ═══════════════════════════════════════════════════════
    const commitState = useCallback((updater) => {
      if (!yWhiteboardRef.current || !yDocRef.current) return;
      yDocRef.current.transact(() => {
        const cur = yWhiteboardRef.current.toArray()[0] || {
          version: "1.0.0", objects: [], background: backgroundColor,
          createdAt: new Date().toISOString(),
        };
        const next = updater(cur);
        if (yWhiteboardRef.current.length === 0) {
          yWhiteboardRef.current.insert(0, [next]);
        } else {
          yWhiteboardRef.current.delete(0, 1);
          yWhiteboardRef.current.insert(0, [next]);
        }
      }, "drawing");
    }, [backgroundColor]);

    const addObject = useCallback((obj) => {
      commitState((cur) => ({
        ...cur,
        objects: [...(cur.objects || []), obj],
        updatedBy: sessionInfo?.streamerId,
        updatedAt: new Date().toISOString(),
      }));
    }, [commitState, sessionInfo]);

    const deleteObject = useCallback((id) => {
      commitState((cur) => ({
        ...cur,
        objects: (cur.objects || []).filter((o) => o.id !== id),
        updatedBy: sessionInfo?.streamerId,
        updatedAt: new Date().toISOString(),
      }));
      if (selectedIdRef.current === id) {
        selectedIdRef.current = null;
        setSelectedId(null);
      }
    }, [commitState, sessionInfo]);

    const updateObject = useCallback((id, patch) => {
      commitState((cur) => ({
        ...cur,
        objects: (cur.objects || []).map((o) => o.id === id ? { ...o, ...patch } : o),
        updatedBy: sessionInfo?.streamerId,
        updatedAt: new Date().toISOString(),
      }));
    }, [commitState, sessionInfo]);

    // 🆕 NAYA LOGIC: Server + Yjs se delete karne ka wrapper function
    const handleDeleteWithCleanup = useCallback(async (id) => {
      if (!id) return;
      const objToDelete = getCurrentObjects().find(o => o.id === id);
      
      // 1. Agar ye server par upload hui image hai, toh API call karke delete karein
      if (objToDelete && objToDelete.type === "image" && objToDelete.src && objToDelete.src.startsWith("http")) {
        try {
          await fetch(`${API_BASE_URL}/delete-image`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageUrl: objToDelete.src }),
          });
          console.log("✅ Image deleted from VPS storage");
        } catch (error) {
          console.error("❌ Failed to delete image from server:", error);
        }
      }

      // 2. Yjs se delete karein (Taki sabki screen se hat jaye)
      deleteObject(id);
    }, [getCurrentObjects, deleteObject]);

    // ═══════════════════════════════════════════════════════
    // Drawing & Hit Test
    // ═══════════════════════════════════════════════════════
    const drawGrid = useCallback((ctx, w, h) => {
      ctx.save();
      ctx.strokeStyle = "#e0e0e0";
      ctx.lineWidth   = 0.5;
      ctx.globalAlpha = 0.3;
      const gs = 20;
      for (let x = 0; x <= w; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y <= h; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
      ctx.restore();
    }, []);

   const drawObject = useCallback((ctx, obj) => {
      if (!obj) return;
      ctx.save();
      ctx.strokeStyle = obj.color       || "#000000";
      ctx.fillStyle   = obj.fillColor   || "transparent";
      ctx.lineWidth   = (obj.strokeWidth || 2) / currentZoom;
      ctx.globalAlpha = obj.opacity     ?? 1;

      switch (obj.type) {
        case "pen":
        case "pencil":
        case "eraser": {
          if (!obj.points?.length) break;
          if (obj.type === "eraser") ctx.globalCompositeOperation = "destination-out";
          ctx.beginPath();
          ctx.moveTo(obj.points[0].x, obj.points[0].y);
          obj.points.forEach((p) => ctx.lineTo(p.x, p.y));
          ctx.stroke();
          break;
        }
        case "line": {
          ctx.beginPath(); ctx.moveTo(obj.x1, obj.y1); ctx.lineTo(obj.x2, obj.y2); ctx.stroke();
          break;
        }
        case "rectangle": {
          if (obj.fillColor) ctx.fillRect(obj.x, obj.y, obj.width, obj.height);
          ctx.strokeRect(obj.x, obj.y, obj.width, obj.height);
          break;
        }
        case "circle": {
          ctx.beginPath(); ctx.arc(obj.x, obj.y, obj.radius, 0, 2 * Math.PI);
          if (obj.fillColor) ctx.fill();
          ctx.stroke();
          break;
        }
        case "text": {
          ctx.font      = `${obj.fontSize || 16}px Arial`;
          ctx.fillStyle = obj.color || "#000";
          ctx.fillText(obj.text || "", obj.x, obj.y);
          break;
        }
        case "image": {
          let img = imageCacheRef.current.get(obj.id);
          if (!img) {
            img = new Image();
            img.crossOrigin = "Anonymous"; 
            
            // 🟢 FIX: URL formatting call here
            // Note: Ensure `getFullImageUrl` is defined at the top of your file.
            const validSrc = typeof getFullImageUrl === 'function' ? getFullImageUrl(obj.src) : obj.src;
            
            img.onload = () => scheduleRedraw(getCurrentObjects());
            
            // 🟢 FIX: Add onerror so silent browser blocks become visible warnings
            img.onerror = () => console.warn("Failed to draw image on canvas:", validSrc);
            
            img.src = validSrc; 
            imageCacheRef.current.set(obj.id, img);
          }
          if (img.complete && img.naturalWidth > 0) {
            ctx.globalAlpha = obj.opacity ?? 1;
            ctx.drawImage(img, obj.x, obj.y, obj.width || DEFAULT_MEDIA_W, obj.height || DEFAULT_MEDIA_H);
          } else {
            ctx.strokeStyle = "#aaa"; ctx.strokeRect(obj.x, obj.y, obj.width || DEFAULT_MEDIA_W, obj.height || DEFAULT_MEDIA_H);
            ctx.fillStyle   = "#eee"; ctx.font = "14px Arial";
            ctx.fillText("Loading image…", obj.x + 8, obj.y + 20);
          }
          break;
        }
        default: break;
      }

      // Selection Highlight & Resize Handle
      if (selectedIdRef.current === obj.id && tool === "select") {
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 2 / currentZoom;
        ctx.setLineDash([6 / currentZoom, 3 / currentZoom]);

        let hx, hy;

        if (["rectangle", "image"].includes(obj.type)) {
          const bw = obj.width || DEFAULT_MEDIA_W;
          const bh = obj.height || DEFAULT_MEDIA_H;
          ctx.strokeRect(obj.x - 2, obj.y - 2, bw + 4, bh + 4);
          hx = obj.x + bw; hy = obj.y + bh;
        } else if (obj.type === "circle") {
          const bw = obj.radius * 2;
          ctx.strokeRect(obj.x - obj.radius - 2, obj.y - obj.radius - 2, bw + 4, bw + 4);
          hx = obj.x + obj.radius * 0.707; hy = obj.y + obj.radius * 0.707;
        } else if (obj.type === "line") {
          hx = obj.x2; hy = obj.y2;
        }

        ctx.setLineDash([]);

        if (hx !== undefined && hy !== undefined) {
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#3b82f6";
          ctx.lineWidth = 2 / currentZoom;
          ctx.beginPath();
          ctx.arc(hx, hy, 6 / currentZoom, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }

      ctx.restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentZoom, tool]);
    const hitTest = useCallback((pt) => {
      const objs = getCurrentObjects();
      for (let i = objs.length - 1; i >= 0; i--) {
        const o = objs[i];
        if (["image", "rectangle"].includes(o.type)) {
          const w = o.width || DEFAULT_MEDIA_W;
          const h = o.height || DEFAULT_MEDIA_H;
          if (pt.x >= o.x && pt.x <= o.x + w && pt.y >= o.y && pt.y <= o.y + h) return o;
        } else if (o.type === "circle") {
          if (Math.hypot(pt.x - o.x, pt.y - o.y) <= o.radius) return o;
        } else if (o.type === "line") {
          if (pointToLineDist(pt, {x: o.x1, y: o.y1}, {x: o.x2, y: o.y2}) < 15 / currentZoom) return o;
        }
      }
      return null;
    }, [getCurrentObjects, currentZoom]);

    const hitTestHandle = useCallback((pt, obj) => {
      let hx, hy;
      if (["rectangle", "image"].includes(obj.type)) {
        hx = obj.x + (obj.width || DEFAULT_MEDIA_W);
        hy = obj.y + (obj.height || DEFAULT_MEDIA_H);
      } else if (obj.type === "circle") {
        hx = obj.x + obj.radius * 0.707; 
        hy = obj.y + obj.radius * 0.707;
      } else if (obj.type === "line") {
        hx = obj.x2; hy = obj.y2;
      } else return false;

      return Math.hypot(pt.x - hx, pt.y - hy) < 15 / currentZoom;
    }, [currentZoom]);

    const redrawCanvas = useCallback((objects) => {
      if (!ctxRef.current || !bgCtxRef.current || !canvasRef.current) return;
      const ctx    = ctxRef.current;
      const bgCtx  = bgCtxRef.current;
      const canvas = canvasRef.current;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      bgCtx.clearRect(0, 0, canvas.width, canvas.height);

      bgCtx.fillStyle = backgroundColor;
      bgCtx.fillRect(0, 0, canvas.width, canvas.height);
      if (isGridVisible) drawGrid(bgCtx, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(canvasOffsetRef.current.x, canvasOffsetRef.current.y);
      ctx.scale(currentZoom, currentZoom);
      (objects || []).forEach((obj) => drawObject(ctx, obj));
      ctx.restore();
    }, [backgroundColor, isGridVisible, currentZoom, drawGrid, drawObject]);

    const scheduleRedraw = useCallback((objects) => {
      pendingObjsRef.current = objects || [];
      if (rafIdRef.current) return;
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        redrawCanvas(pendingObjsRef.current || []);
        pendingObjsRef.current = null;
      });
    }, [redrawCanvas]);

    const loadWhiteboardState = useCallback(() => {
      if (!yWhiteboardRef.current || yWhiteboardRef.current.length === 0) return;
      try {
        const state = yWhiteboardRef.current.toArray()[0] || {};
        if (state.background) setBackgroundColor(state.background);
        scheduleRedraw(Array.isArray(state.objects) ? state.objects : []);
      } catch (e) { console.error(e); }
    }, [scheduleRedraw]);

    // ═══════════════════════════════════════════════════════
    // Smooth stroke & Shape flushing
    // ═══════════════════════════════════════════════════════
    const drawSmoothStroke = useCallback((prev, next, strokeType) => {
      if (!ctxRef.current) return;
      const ctx = ctxRef.current;
      ctx.save();
      ctx.translate(canvasOffsetRef.current.x, canvasOffsetRef.current.y);
      ctx.scale(currentZoom, currentZoom);
      ctx.lineCap   = "round";
      ctx.lineJoin  = "round";
      ctx.globalAlpha = opacity ?? 1;
      ctx.lineWidth   = (strokeWidth || 2) / currentZoom;

      if (strokeType === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = color || "#000000";
      }

      const mid = { x: (prev.x + next.x) / 2, y: (prev.y + next.y) / 2 };
      if (!lastLocalMidRef.current) lastLocalMidRef.current = { x: prev.x, y: prev.y };
      ctx.beginPath();
      ctx.moveTo(lastLocalMidRef.current.x, lastLocalMidRef.current.y);
      ctx.quadraticCurveTo(prev.x, prev.y, mid.x, mid.y);
      ctx.stroke();
      lastLocalMidRef.current = mid;
      ctx.restore();
    }, [currentZoom, color, strokeWidth, opacity]);

    const flushStrokeToYjs = useCallback(() => {
      const strokeId = currentStrokeIdRef.current;
      if (!strokeId) return;
      const buffered = strokeBufferRef.current;
      if (!buffered.length) return;
      const pts = buffered.slice();
      strokeBufferRef.current = [];

      commitState((cur) => {
        const objs   = [...(cur.objects || [])];
        const idx    = objs.findIndex((o) => o?.id === strokeId);
        if (idx === -1) return cur;
        const target = { ...objs[idx] };
        target.points = [...(target.points || []), ...pts];
        objs[idx] = target;
        return { ...cur, objects: objs, updatedAt: new Date().toISOString() };
      });
    }, [commitState]);

    const flushShapeToYjs = useCallback((currentPoint) => {
      const strokeId = currentStrokeIdRef.current;
      const startP = shapeStartRef.current;
      if (!strokeId || !startP || !currentPoint) return;

      commitState((cur) => {
        const objs   = [...(cur.objects || [])];
        const idx    = objs.findIndex((o) => o?.id === strokeId);
        if (idx === -1) return cur;
        const target = { ...objs[idx] };
        
        if (target.type === "rectangle") {
          target.width = currentPoint.x - startP.x;
          target.height = currentPoint.y - startP.y;
        } else if (target.type === "circle") {
          target.radius = Math.hypot(currentPoint.x - startP.x, currentPoint.y - startP.y);
        } else if (target.type === "line") {
          target.x2 = currentPoint.x;
          target.y2 = currentPoint.y;
        }
        
        objs[idx] = target;
        return { ...cur, objects: objs, updatedAt: new Date().toISOString() };
      });
    }, [commitState]);

    const scheduleFlush = useCallback(() => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        if (tool === "pen" || tool === "eraser") {
          flushStrokeToYjs();
        } else if (["line", "rectangle", "circle"].includes(tool)) {
          flushShapeToYjs(lastPointRef.current);
        }
      }, FLUSH_INTERVAL);
    }, [flushStrokeToYjs, flushShapeToYjs, tool]);

    // ═══════════════════════════════════════════════════════
    // Yjs init
    // ═══════════════════════════════════════════════════════
    useEffect(() => {
      if (!sessionId || !wsToken) return;

      let isComponentMounted = true; 

      const initYjs = async () => {
        try {
          const ydoc      = new Y.Doc();
          yDocRef.current = ydoc;

          const baseWs   = import.meta.env.VITE_WS_URL || "ws://localhost:9090";
          const provider = new WebsocketProvider(`${baseWs}/yjs`, sessionId, ydoc, {
            WebSocketPolyfill: WebSocket,
            params: {
              token: wsToken, isStreamer: true, allowViewersToDraw,
              roomCode, userId: sessionInfo?.streamerId, userName: sessionInfo?.streamerName,
            },
          });
          yProviderRef.current  = provider;

          const yWhiteboard      = ydoc.getArray("whiteboard");
          yWhiteboardRef.current = yWhiteboard;

          const ySettings        = ydoc.getMap("room_settings");
          ySettingsRef.current   = ySettings;

          provider.awareness.setLocalState({
            userId: sessionInfo?.streamerId || "streamer",
            userName: sessionInfo?.streamerName || "Streamer",
            role: "STREAMER", isStreamer: true, color, tool, cursor: null,
          });

          provider.awareness.on("change", () => {
            if (!isComponentMounted) return; 
            try {
              const states = Array.from(provider.awareness.getStates().entries());
              setParticipants(states.map(([id, s]) => ({ clientId: id, ...s })).filter((p) => p.userId));
            } catch (err) {
              console.warn("Awareness update error ignored:", err);
            }
          });

          provider.on("sync", (synced) => {
            if (!isComponentMounted) return; 
            setIsConnected(!!synced);
            if (synced) loadWhiteboardState();
          });

          ydoc.on('update', (update, origin) => {
            try {} catch (err) {
               console.warn("⚠️ Ignored corrupted YJS update payload", err);
            }
          });

          yUndoManagerRef.current = new Y.UndoManager(yWhiteboard, {
            captureTimeout: 150, trackedOrigins: new Set(["drawing"]),
          });

          new IndexeddbPersistence(`whiteboard-${sessionId}`, ydoc);

          const observer = (event) => {
            if (!isComponentMounted) return;
            const origin = event?.transaction?.origin;
            if (origin === "drawing" && (isDrawing || currentStrokeIdRef.current)) return;
            loadWhiteboardState();
          };
          yWhiteboard.observe(observer);
          yObserverCleanup.current = () => { try { yWhiteboard.unobserve(observer); } catch {} };

        } catch (err) {
          console.error("Failed to init Yjs:", err);
          toast.error("Failed to connect to whiteboard server");
        }
      };

      initYjs();

      return () => {
        isComponentMounted = false; 
        
        try { 
          if (tool === "pen" || tool === "eraser") flushStrokeToYjs(true); 
          else flushShapeToYjs(lastPointRef.current);
        } catch {}
        
        if (yObserverCleanup.current) { 
           yObserverCleanup.current(); 
           yObserverCleanup.current = null; 
        }

        const p = yProviderRef.current;
        const d = yDocRef.current;
        yProviderRef.current = null;
        yDocRef.current = null;
        yWhiteboardRef.current = null;

        setTimeout(() => {
          if (p) { try { p.disconnect(); p.destroy(); } catch (e) { } }
          if (d) { try { d.destroy(); } catch (e) { } }
        }, 100);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, roomCode, wsToken, allowViewersToDraw, sessionInfo]);
    
    useEffect(() => {
      const p = yProviderRef.current;
      if (!p) return;
      try {
        const prev = p.awareness.getLocalState() || {};
        p.awareness.setLocalState({ ...prev, color, tool,
          userId: sessionInfo?.streamerId || "streamer",
          userName: sessionInfo?.streamerName || "Streamer",
          role: "STREAMER", isStreamer: true,
        });
      } catch {}
    }, [color, tool, sessionInfo]);

    // cleanup timers
    useEffect(() => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (rafIdRef.current)      cancelAnimationFrame(rafIdRef.current);
    }, []);

    // ═══════════════════════════════════════════════════════
    // Canvas init + resize
    // ═══════════════════════════════════════════════════════
    useEffect(() => {
      if (!canvasRef.current || !backgroundCanvasRef.current || !containerRef.current) return;
      const canvas    = canvasRef.current;
      const bgCanvas  = backgroundCanvasRef.current;
      const container = containerRef.current;

      const resize = () => {
        canvas.width  = bgCanvas.width  = container.clientWidth;
        canvas.height = bgCanvas.height = container.clientHeight;
        const ctx   = canvas.getContext("2d");
        const bgCtx = bgCanvas.getContext("2d");
        ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctxRef.current = ctx; bgCtxRef.current = bgCtx;
        
        canvas.width = BASE_W;
        canvas.height = BASE_H;
        bgCanvas.width = BASE_W;
        bgCanvas.height = BASE_H;
        
        if (yWhiteboardRef.current) {
          const s = yWhiteboardRef.current.toArray()[0] || {};
          scheduleRedraw(s.objects || []);
        }
      };

      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(container);
      return () => ro.disconnect();
    }, [scheduleRedraw]);

    // ═══════════════════════════════════════════════════════
    // Paste handler — Ctrl+V image (UPDATE KIYA GAYA HAI)
    // ═══════════════════════════════════════════════════════
    useEffect(() => {
      const handlePaste = async (e) => {
        for (const item of e.clipboardData.items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            const formData = new FormData();
            formData.append("whiteboardImage", file);

            try {
              toast.info("Pasting image...");
              const response = await fetch(`${API_BASE_URL}/upload-image`, {
                method: "POST",
                body: formData,
              });
              const data = await response.json();
              
              if (data.url) {
                // Dimensons nikalne ke liye
                const img = new Image();
                img.onload = () => {
                  const ratio = img.naturalWidth / img.naturalHeight;
                  const w     = Math.min(img.naturalWidth, DEFAULT_MEDIA_W);
                  const h     = w / ratio;
                  addObject({ id: uid(), type: "image", src: data.url, x: 80, y: 80, width: w, height: h, opacity: 1, timestamp: Date.now() });
                };
                img.src = data.url;
              }
            } catch (err) {
              console.error("Paste upload failed:", err);
              toast.error("Failed to paste image");
            }
            break;
          }
        }
      };
      window.addEventListener("paste", handlePaste);
      return () => window.removeEventListener("paste", handlePaste);
    }, [addObject]);

    // ═══════════════════════════════════════════════════════
    // Keyboard — Delete selected (UPDATE KIYA GAYA HAI)
    // ═══════════════════════════════════════════════════════
    useEffect(() => {
      const handleKey = (e) => {
        if ((e.key === "Delete" || e.key === "Backspace") && selectedIdRef.current) {
          if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
          // 🆕 UPDATE: Delete from server + yjs
          handleDeleteWithCleanup(selectedIdRef.current);
        }
        if ((e.ctrlKey || e.metaKey) && e.key === "z") {
          e.preventDefault();
          yUndoManagerRef.current?.undo();
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) {
          e.preventDefault();
          yUndoManagerRef.current?.redo();
        }
      };
      window.addEventListener("keydown", handleKey);
      return () => window.removeEventListener("keydown", handleKey);
    }, [handleDeleteWithCleanup]);

    // ═══════════════════════════════════════════════════════
    // Image upload handler (UPDATE KIYA GAYA HAI)
    // ═══════════════════════════════════════════════════════
    // const handleImageUpload = useCallback(async (e) => {
    //   const file = e.target.files?.[0];
    //   if (!file) return;
    //   if (file.size > 10 * 1024 * 1024) { toast.error("Image must be < 10MB"); return; }
      
    //   const formData = new FormData();
    //   formData.append("whiteboardImage", file);

    //   try {
    //     toast.info("Uploading image...");
    //     const response = await fetch(`${API_BASE_URL}/upload-image`, {
    //       method: "POST",
    //       body: formData,
    //     });

    //     if (!response.ok) throw new Error("Upload failed");
        
    //     const data = await response.json();
    //     const imageUrl = data.url;

    //     // Display karwane ke liye add karein
    //     const img = new Image();
    //     img.onload = () => {
    //       const ratio = img.naturalWidth / img.naturalHeight;
    //       const w     = Math.min(img.naturalWidth, DEFAULT_MEDIA_W);
    //       const h     = w / ratio;
    //       addObject({ id: uid(), type: "image", src: imageUrl, x: 80, y: 80, width: w, height: h, opacity: 1, timestamp: Date.now() });
    //       toast.success("Image added!");
    //     };
    //     img.src = imageUrl;
    //   } catch (error) {
    //     console.error("Upload error:", error);
    //     toast.error("Failed to upload image");
    //   }

    //   e.target.value = "";
    // }, [addObject]);

    const handleImageUpload = useCallback(async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error("Image must be < 10MB"); return; }
      
      const formData = new FormData();
      formData.append("whiteboardImage", file);

      try {
        toast.info("Uploading image...");
        const response = await fetch(`${API_BASE_URL}/upload-image`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error("Upload failed");
        
        const data = await response.json();
        const imageUrl = getFullImageUrl(data.url); // 🟢 Use formatted URL

        const img = new Image();
        // 🟢 FIX 2: Yahan par bhi Anonymous lagana zaroori hai!
        img.crossOrigin = "Anonymous"; 
        
        img.onload = () => {
          const ratio = img.naturalWidth / img.naturalHeight;
          const w     = Math.min(img.naturalWidth, DEFAULT_MEDIA_W);
          const h     = w / ratio;
          addObject({ id: uid(), type: "image", src: imageUrl, x: 80, y: 80, width: w, height: h, opacity: 1, timestamp: Date.now() });
          toast.success("Image added!");
        };
        img.onerror = () => toast.error("Failed to load image preview");
        img.src = imageUrl;
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload image");
      }

      e.target.value = "";
    }, [addObject]);

    // ═══════════════════════════════════════════════════════
    // Pointer events
    // ═══════════════════════════════════════════════════════
    const handlePointerDown = useCallback((e) => {
      e.preventDefault();
      if (!canvasRef.current || e.button === 2) return;
      try { canvasRef.current.setPointerCapture(e.pointerId); pointerIdRef.current = e.pointerId; } catch {}

      if (tool === "pan" || e.altKey || e.button === 1) {
        setIsPanning(true);
        lastPanPointRef.current = { x: e.clientX, y: e.clientY };
        canvasRef.current.style.cursor = "grabbing";
        return;
      }

      const p = getTransformedPoint(e);

      // Select / Move / Resize tool
      if (tool === "select") {
        const objs = getCurrentObjects();
        const selObj = objs.find(o => o.id === selectedIdRef.current);
        
        // 1. Check if Handle is clicked (Resize)
        if (selObj && hitTestHandle(p, selObj)) {
          isResizingRef.current = true;
          return;
        }

        // 2. Check if Object is clicked (Drag/Move)
        const hit = hitTest(p);
        if (hit) {
          selectedIdRef.current = hit.id;
          setSelectedId(hit.id);
          dragOffsetRef.current = { 
            x: p.x - (hit.type === 'line' ? hit.x1 : hit.x), 
            y: p.y - (hit.type === 'line' ? hit.y1 : hit.y) 
          };
          isDraggingRef.current = true;
        } else {
          selectedIdRef.current = null;
          setSelectedId(null);
        }
        scheduleRedraw(getCurrentObjects());
        return;
      }

      setIsDrawing(true);
      lastPointRef.current      = p;
      lastLocalPointRef.current = p;
      lastLocalMidRef.current   = null;
      shapeStartRef.current     = p; // Set the origin point for shapes

      const strokeId = uid();
      currentStrokeIdRef.current = strokeId;

      if (tool === "pen" || tool === "eraser") {
        strokeBufferRef.current    = [];
        addObject({
          id: strokeId, type: tool,
          points: [{ x: p.x, y: p.y }],
          color: tool === "eraser" ? backgroundColor : color,
          strokeWidth, opacity, timestamp: Date.now(),
        });
        drawSmoothStroke(p, { x: p.x + 0.01, y: p.y + 0.01 }, tool);
      } else if (["line", "rectangle", "circle"].includes(tool)) {
         const baseObj = {
            id: strokeId, type: tool,
            x: p.x, y: p.y,
            color, strokeWidth, opacity, timestamp: Date.now()
         };
         if (tool === "line") { baseObj.x1 = p.x; baseObj.y1 = p.y; baseObj.x2 = p.x; baseObj.y2 = p.y; }
         if (tool === "rectangle") { baseObj.width = 0; baseObj.height = 0; }
         if (tool === "circle") { baseObj.radius = 0; }
         addObject(baseObj);
      }
    }, [tool, getTransformedPoint, hitTestHandle, hitTest, addObject, drawSmoothStroke, scheduleRedraw, getCurrentObjects, backgroundColor, color, strokeWidth, opacity]);

    const handlePointerMove = useCallback((e) => {
      if (!canvasRef.current) return;

      if (isPanning) {
        const dx = e.clientX - lastPanPointRef.current.x;
        const dy = e.clientY - lastPanPointRef.current.y;
        canvasOffsetRef.current.x += dx;
        canvasOffsetRef.current.y += dy;
        lastPanPointRef.current = { x: e.clientX, y: e.clientY };
        scheduleRedraw(getCurrentObjects());
        return;
      }

      const p = getTransformedPoint(e);

      // Drag or Resize media object
      if (tool === "select" && selectedIdRef.current) {
        if (isResizingRef.current) {
          const objs = getCurrentObjects();
          const selObj = objs.find(o => o.id === selectedIdRef.current);
          if (!selObj) return;

          let patch = {};
          if (["rectangle", "image"].includes(selObj.type)) {
            patch.width = Math.max(20, p.x - selObj.x);
            patch.height = Math.max(20, p.y - selObj.y);
          } else if (selObj.type === "circle") {
            patch.radius = Math.max(10, Math.hypot(p.x - selObj.x, p.y - selObj.y));
          } else if (selObj.type === "line") {
            patch.x2 = p.x;
            patch.y2 = p.y;
          }
          pendingUpdateRef.current = patch;

        } else if (isDraggingRef.current) {
          const selObj = getCurrentObjects().find(o => o.id === selectedIdRef.current);
          if (!selObj) return;

          let patch = {};
          const newX = p.x - dragOffsetRef.current.x;
          const newY = p.y - dragOffsetRef.current.y;

          if (selObj.type === "line") {
            const dx = newX - selObj.x1;
            const dy = newY - selObj.y1;
            patch.x1 = newX;
            patch.y1 = newY;
            patch.x2 = selObj.x2 + dx;
            patch.y2 = selObj.y2 + dy;
          } else {
            patch.x = newX;
            patch.y = newY;
          }
          pendingUpdateRef.current = patch;
        }

        if (pendingUpdateRef.current) {
          const tempObjs = getCurrentObjects().map(o => o.id === selectedIdRef.current ? { ...o, ...pendingUpdateRef.current } : o);
          redrawCanvas(tempObjs);

          if (!flushTimerRef.current) {
            flushTimerRef.current = setTimeout(() => {
                flushTimerRef.current = null;
                if (pendingUpdateRef.current) {
                    updateObject(selectedIdRef.current, pendingUpdateRef.current);
                    pendingUpdateRef.current = null;
                }
            }, FLUSH_INTERVAL);
          }
        }
        return;
      }

      if (!isDrawing) return;

      if (tool === "pen" || tool === "eraser") {
        const events = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [e];
        for (const evt of events) {
          const next = getTransformedPoint(evt);
          const prev = lastLocalPointRef.current || lastPointRef.current;
          const dist = prev ? Math.hypot(next.x - prev.x, next.y - prev.y) : 999;
          if (dist < MIN_MOVE_DIST) continue;
          if (prev) drawSmoothStroke(prev, next, tool);
          strokeBufferRef.current.push(next);
          scheduleFlush();
          lastLocalPointRef.current = next;
          lastPointRef.current      = next;
        }
      } else if (["line", "rectangle", "circle"].includes(tool)) {
        lastPointRef.current = p;

        redrawCanvas(getCurrentObjects());
        const ctx = ctxRef.current;
        ctx.save();
        ctx.translate(canvasOffsetRef.current.x, canvasOffsetRef.current.y);
        ctx.scale(currentZoom, currentZoom);
        ctx.strokeStyle = color;
        ctx.lineWidth = (strokeWidth || 2) / currentZoom;
        ctx.globalAlpha = opacity ?? 1;

        if (tool === "rectangle") {
            ctx.strokeRect(shapeStartRef.current.x, shapeStartRef.current.y, p.x - shapeStartRef.current.x, p.y - shapeStartRef.current.y);
        } else if (tool === "circle") {
            ctx.beginPath();
            ctx.arc(shapeStartRef.current.x, shapeStartRef.current.y, Math.hypot(p.x - shapeStartRef.current.x, p.y - shapeStartRef.current.y), 0, Math.PI * 2);
            ctx.stroke();
        } else if (tool === "line") {
            ctx.beginPath();
            ctx.moveTo(shapeStartRef.current.x, shapeStartRef.current.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
        }
        ctx.restore();

        if (!flushTimerRef.current) {
          flushTimerRef.current = setTimeout(() => {
              flushTimerRef.current = null;
              flushShapeToYjs(lastPointRef.current);
          }, FLUSH_INTERVAL);
        }
      }
    }, [isDrawing, isPanning, tool, getTransformedPoint, drawSmoothStroke, scheduleFlush, flushShapeToYjs, scheduleRedraw, getCurrentObjects, updateObject, redrawCanvas, color, strokeWidth, opacity, currentZoom]);

    const endStrokeCleanup = useCallback(() => {
      if (tool === "pen" || tool === "eraser") {
         flushStrokeToYjs(true);
      } else if (["line", "rectangle", "circle"].includes(tool)) {
         flushShapeToYjs(lastPointRef.current);
      } else if (tool === "select" && pendingUpdateRef.current) {
         updateObject(selectedIdRef.current, pendingUpdateRef.current);
         pendingUpdateRef.current = null;
      }
      
      currentStrokeIdRef.current = null;
      strokeBufferRef.current    = [];
      lastLocalPointRef.current  = null;
      lastLocalMidRef.current    = null;
      shapeStartRef.current      = null;
      isDraggingRef.current      = false;
      isResizingRef.current      = false;

      if (canvasRef.current && pointerIdRef.current != null) {
        try { canvasRef.current.releasePointerCapture(pointerIdRef.current); } catch {}
      }
      pointerIdRef.current = null;
    }, [flushStrokeToYjs, flushShapeToYjs, tool, updateObject]);

    const handlePointerUp    = useCallback(() => { setIsDrawing(false); setIsPanning(false); endStrokeCleanup(); if (canvasRef.current) canvasRef.current.style.cursor = tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair"; }, [tool, endStrokeCleanup]);
    const handlePointerLeave = useCallback(() => { setIsDrawing(false); setIsPanning(false); endStrokeCleanup(); }, [endStrokeCleanup]);

    // ── wheel zoom ──
    const handleWheel = useCallback((e) => {
      e.preventDefault();
      if (!canvasRef.current) return;
      const rect   = canvasRef.current.getBoundingClientRect();
      const scaleX = BASE_W / rect.width;
      const scaleY = BASE_H / rect.height;
      const mx     = (e.clientX - rect.left) * scaleX;
      const my     = (e.clientY - rect.top) * scaleY;
      
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZ   = Math.max(0.5, Math.min(3, currentZoom * factor));
      const change = newZ / currentZoom;
      
      canvasOffsetRef.current.x = mx - (mx - canvasOffsetRef.current.x) * change;
      canvasOffsetRef.current.y = my - (my - canvasOffsetRef.current.y) * change;
      setCurrentZoom(newZ);
      scheduleRedraw(getCurrentObjects());
    }, [currentZoom, scheduleRedraw, getCurrentObjects]);

    const handleZoomIn    = useCallback(() => { setCurrentZoom((p) => Math.min(p + 0.1, 3));  scheduleRedraw(getCurrentObjects()); }, [scheduleRedraw, getCurrentObjects]);
    const handleZoomOut   = useCallback(() => { setCurrentZoom((p) => Math.max(p - 0.1, 0.5)); scheduleRedraw(getCurrentObjects()); }, [scheduleRedraw, getCurrentObjects]);
    const handleZoomReset = useCallback(() => { setCurrentZoom(1); canvasOffsetRef.current = { x: 0, y: 0 }; scheduleRedraw(getCurrentObjects()); }, [scheduleRedraw, getCurrentObjects]);

    const cursorStyle = tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair";

    // ═══════════════════════════════════════════════════════
    // Whiteboard actions
    // ═══════════════════════════════════════════════════════
    const handleClearWhiteboard = useCallback(() => {
      if (!yWhiteboardRef.current || !yDocRef.current) return;
      if (!window.confirm("Clear entire whiteboard?")) return;
      yDocRef.current.transact(() => {
        yWhiteboardRef.current.delete(0, yWhiteboardRef.current.length);
        yWhiteboardRef.current.insert(0, [{
          version: "1.0.0", objects: [], background: backgroundColor,
          clearedAt: new Date().toISOString(), clearedBy: sessionInfo?.streamerId,
        }]);
      }, "drawing");
      
      // OPTIONAL: Call delete API on all deleted images here if you want to fully clear server storage

      imageCacheRef.current.clear();
      scheduleRedraw([]);
      toast.success("Whiteboard cleared");
    }, [backgroundColor, sessionInfo, scheduleRedraw]);

    const handleExport = useCallback(() => {
      if (!canvasRef.current || !backgroundCanvasRef.current) return;
      const exp = document.createElement("canvas");
      exp.width  = canvasRef.current.width;
      exp.height = canvasRef.current.height;
      const ctx  = exp.getContext("2d");
      ctx.drawImage(backgroundCanvasRef.current, 0, 0);
      ctx.drawImage(canvasRef.current,           0, 0);
      const link     = document.createElement("a");
      link.download  = `whiteboard-${sessionId}-${Date.now()}.png`;
      link.href      = exp.toDataURL("image/png");
      link.click();
      toast.success("Whiteboard exported");
    }, [sessionId]);

    const handleUndo = useCallback(() => yUndoManagerRef.current?.undo(), []);
    const handleRedo = useCallback(() => yUndoManagerRef.current?.redo(), []);

    const handleDeleteSelected = useCallback(() => {
      // 🆕 UPDATE: Delete via API too
      if (selectedIdRef.current) handleDeleteWithCleanup(selectedIdRef.current);
    }, [handleDeleteWithCleanup]);

    // ═══════════════════════════════════════════════════════
    // Render
    // ═══════════════════════════════════════════════════════
    return (
      <div
        ref={containerRef}
        className={`absolute inset-0 z-20 w-full h-full bg-gray-900 overflow-hidden ${isActive ? "block" : "hidden"}`}
        onWheel={handleWheel}
      >
        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        {/* Background canvas */}
        <canvas ref={backgroundCanvasRef} className="absolute top-0 left-0 w-full h-full" style={{ pointerEvents: "none" }} />

        {/* Main drawing canvas */}
        <canvas
          ref={canvasRef}
          className={`absolute top-0 left-0 w-full h-full`}
          style={{ touchAction: "none", cursor: cursorStyle, zIndex: 20 }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* ── Top Toolbar ── */}
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-30">
          <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-700 px-2 py-1.5 flex items-center gap-1.5 flex-wrap">

            {/* Connection status */}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isConnected ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
            <span className="text-white text-xs mr-1">{isConnected ? "Live" : "Offline"}</span>

            <div className="w-px h-5 bg-gray-600" />

            {/* Draw tools */}
            {[
              { t: "pen",       icon: <FaPaintBrush className="w-3.5 h-3.5" />,   title: "Pen" },
              { t: "eraser",    icon: <FaEraser     className="w-3.5 h-3.5" />,   title: "Eraser" },
              { t: "line",      icon: <FiMinus      className="w-3.5 h-3.5" />,   title: "Line" },
              { t: "rectangle", icon: <FiSquare     className="w-3.5 h-3.5" />,   title: "Rectangle" },
              { t: "circle",    icon: <FiCircle     className="w-3.5 h-3.5" />,   title: "Circle" },
              { t: "select",    icon: <FiMousePointer className="w-3.5 h-3.5" />, title: "Select / Move / Resize" },
              { t: "pan",       icon: <FiMove       className="w-3.5 h-3.5" />,   title: "Pan (Alt+Drag)" },
            ].map(({ t, icon, title }) => (
              <button key={t} onClick={() => setTool(t)} title={title}
                className={`p-1.5 rounded-lg transition-colors ${tool === t ? "bg-blue-600 text-white" : "bg-gray-700 hover:bg-gray-600 text-gray-300"}`}>
                {icon}
              </button>
            ))}

            <div className="w-px h-5 bg-gray-600" />

            {/* Color + stroke */}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="w-6 h-6 rounded cursor-pointer border border-gray-600" title="Color" />
            <select value={strokeWidth} onChange={(e) => setStrokeWidth(Number(e.target.value))}
              className="bg-gray-700 text-white text-xs rounded px-1.5 py-1 border border-gray-600 w-14">
              {[1,2,3,5,8].map((v) => <option key={v} value={v}>{v}px</option>)}
            </select>
            <select value={opacity} onChange={(e) => setOpacity(Number(e.target.value))}
              className="bg-gray-700 text-white text-xs rounded px-1.5 py-1 border border-gray-600 w-14">
              {[1,0.8,0.6,0.4].map((v) => <option key={v} value={v}>{v*100}%</option>)}
            </select>

            <div className="w-px h-5 bg-gray-600" />

            {/* Image insert button */}
            <button onClick={() => fileInputRef.current?.click()} title="Upload Image"
              className="p-1.5 bg-gray-700 hover:bg-green-700 rounded-lg transition-colors text-gray-300 hover:text-white">
              <FiImage className="w-3.5 h-3.5" />
            </button>

            <div className="w-px h-5 bg-gray-600" />

            {/* Delete selected */}
            {selectedId && (
              <button onClick={handleDeleteSelected} title="Delete selected (Del)"
                className="p-1.5 bg-red-700 hover:bg-red-600 rounded-lg transition-colors text-white">
                <FiTrash2 className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Zoom */}
            <button onClick={handleZoomOut}   title="Zoom Out"  className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg"><FiMinusCircle className="w-3.5 h-3.5 text-white"/></button>
            <span className="text-white text-xs min-w-[44px] text-center">{Math.round(currentZoom * 100)}%</span>
            <button onClick={handleZoomIn}    title="Zoom In"   className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg"><FiPlus className="w-3.5 h-3.5 text-white"/></button>
            <button onClick={handleZoomReset} title="Reset Zoom" className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg"><FiRefreshCcw className="w-3.5 h-3.5 text-white"/></button>

            <div className="w-px h-5 bg-gray-600" />

            {/* Undo/Redo */}
            <button onClick={handleUndo} title="Undo (Ctrl+Z)"             className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg"><FiRefreshCcw className="w-3.5 h-3.5 text-white"/></button>
            <button onClick={handleRedo} title="Redo (Ctrl+Y)"             className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg"><FiRefreshCcw className="w-3.5 h-3.5 text-white transform scale-x-[-1]"/></button>

            {/* Grid */}
            <button onClick={() => setIsGridVisible((v) => !v)} title="Toggle Grid"
              className={`p-1.5 rounded-lg transition-colors ${isGridVisible ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}>
              <FiSquare className="w-3.5 h-3.5" />
            </button>

            {/* Clear + Export */}
            <button onClick={handleClearWhiteboard} title="Clear All" className="p-1.5 bg-red-900 hover:bg-red-800 rounded-lg"><FiTrash2   className="w-3.5 h-3.5 text-white"/></button>
            <button onClick={handleExport}          title="Export PNG" className="p-1.5 bg-blue-900 hover:bg-blue-800 rounded-lg"><FiDownload className="w-3.5 h-3.5 text-white"/></button>

            <div className="w-px h-5 bg-gray-600" />
            <button onClick={onClose} title="Close" className="p-1.5 bg-red-600 hover:bg-red-700 rounded-lg"><FiX className="w-3.5 h-3.5 text-white"/></button>
          </div>
        </div>

        {/* ── Bottom status bar ── */}
        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm border border-gray-700">
          <div className="flex items-center gap-3">
            <span>Tool: <span className="font-bold capitalize">{tool}</span></span>
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span>Zoom: <span className="font-bold">{Math.round(currentZoom * 100)}%</span></span>
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span>Viewers: <span className="font-bold">{participants.length}</span></span>
            {selectedId && <><span className="w-1 h-1 bg-gray-500 rounded-full" /><span className="text-blue-400">Object selected — Del to remove</span></>}
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span className="text-gray-400">Ctrl+V to paste image</span>
          </div>
        </div>

        {/* ── Viewers list ── */}
        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm border border-gray-700">
          <div className="flex items-center gap-2">
            <span>{participants.length} viewer{participants.length !== 1 ? "s" : ""}</span>
            {participants.map((p) => (
              <div key={p.clientId} className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-[10px] font-medium" title={p.userName || "User"}>
                {(p.userName || "U").charAt(0)}
              </div>
            ))}
          </div>
        </div>

        {isPanning && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white px-4 py-2 rounded-lg backdrop-blur-sm border border-gray-600 flex items-center gap-2 pointer-events-none">
            <FiMove className="w-4 h-4" /> <span>Panning…</span>
          </div>
        )}
      </div>
    );
  },
  (prev, next) => {
    if (prev.sessionId         !== next.sessionId)         return false;
    if (prev.roomCode          !== next.roomCode)          return false;
    if (prev.wsToken           !== next.wsToken)           return false;
    if (prev.isActive          !== next.isActive)          return false;
    if (prev.allowViewersToDraw !== next.allowViewersToDraw) return false;
    if (prev.mainScreenMode    !== next.mainScreenMode)    return false;
    if (prev.compact           !== next.compact)           return false;
    if (prev.sessionInfo?.streamerId   !== next.sessionInfo?.streamerId)   return false;
    if (prev.sessionInfo?.streamerName !== next.sessionInfo?.streamerName) return false;
    return true;
  }
);

StreamerWhiteboard.displayName = "StreamerWhiteboard";
export default StreamerWhiteboard;


